import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import styles from "./dropdownfilter.module.css";
import DropDownIcon from "../Icons/DropDownIcon";
import SearchBar from "../Search/SearchBar";

const DropdownNoImage = ({
	options,
	defaultOption,
	onSelect,
	selectedLabel,
	selectedValue,
	dropdownClass,
	dropdownMenuClass,
	labelText = "Display: ",
	placeholder = "Select an option",
	placeholderClass = styles.placeholder,
	dropdownToggleClass = "",
	showGenderCircle = false,
	dropdownOptionsClass = "",
	onOpen,
	enableSearch = false,
	searchPlaceholder = "Search...",
}) => {
	const [selectedOption, setSelectedOption] = useState(defaultOption);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [keyboardSelectedIndex, setKeyboardSelectedIndex] = useState(-1);
	const [searchQuery, setSearchQuery] = useState("");
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null);
	const searchInputRef = useRef(null);

	const getSearchableTokens = useCallback((value) => {
		const normalized = String(value || "").toLowerCase().trim();
		if (!normalized) return [""];

		const compact = normalized.replace(/\s+/g, " ");
		const noSpace = compact.replace(/\s+/g, "");
		const tokens = new Set([compact, noSpace]);

		const timeMatch = compact.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/);
		if (timeMatch) {
			const hour = Number(timeMatch[1]);
			const minute = timeMatch[2];
			const meridiem = timeMatch[3] || "";
			if (!Number.isNaN(hour)) {
				const hourNoPad = String(hour);
				const hourPadded = hourNoPad.padStart(2, "0");
				const meridiemWithSpace = meridiem ? ` ${meridiem}` : "";
				const meridiemNoSpace = meridiem || "";

				tokens.add(`${hourNoPad}:${minute}${meridiemWithSpace}`);
				tokens.add(`${hourPadded}:${minute}${meridiemWithSpace}`);
				tokens.add(`${hourNoPad}:${minute}${meridiemNoSpace}`);
				tokens.add(`${hourPadded}:${minute}${meridiemNoSpace}`);
			}
		}

		return Array.from(tokens);
	}, []);

	const getTokenMatchScore = useCallback((labelToken, queryToken) => {
		if (!queryToken) return 0;
		if (labelToken === queryToken) return 400; // exact match
		if (labelToken.startsWith(queryToken)) return 300; // best partial
		const wordBoundaryRegex = new RegExp(`(^|\\s)${queryToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`);
		if (wordBoundaryRegex.test(labelToken)) return 200; // phrase boundary
		if (labelToken.includes(queryToken)) return 100; // fallback substring
		return 0;
	}, []);

	const filteredOptions = useMemo(() => {
		if (!enableSearch) return options;
		if (!searchQuery.trim()) return options;
		const searchTokens = getSearchableTokens(searchQuery);
		const matchesWithScore = options.map((option, index) => {
			const label = typeof option === "string" ? option : option.label;
			const labelTokens = getSearchableTokens(label);
			let bestScore = 0;

			searchTokens.forEach((queryToken) => {
				labelTokens.forEach((labelToken) => {
					bestScore = Math.max(bestScore, getTokenMatchScore(labelToken, queryToken));
				});
			});

			return { option, index, score: bestScore };
		});

		return matchesWithScore
			.filter((item) => item.score > 0)
			.sort((a, b) => b.score - a.score || a.index - b.index)
			.map((item) => item.option);
	}, [options, searchQuery, enableSearch, getSearchableTokens, getTokenMatchScore]);

	useEffect(() => {
		setSelectedOption(selectedLabel || defaultOption);
	}, [selectedLabel, defaultOption]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setDropdownOpen(false);
				if (enableSearch) {
					setSearchQuery("");
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [enableSearch]);

	// Auto-scroll dropdown into view inside scrollable parent
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	useEffect(() => {
		if (!isDropdownOpen || !dropdownMenuRef.current) return;

		const selectedElement = dropdownMenuRef.current.querySelector(`[data-selected="true"]`);
		if (selectedElement) {
			selectedElement.scrollIntoView({
				block: "center",
			});
		}
	}, [isDropdownOpen, selectedValue]);

	const handleSelect = useCallback((option) => {
		const selected = typeof option === "string" ? option : option.label;
		// Call onSelect first and check if selection should proceed
		const result = onSelect(option);
		// Only update internal state if onSelect doesn't explicitly return false
		if (result !== false) {
			setSelectedOption(selected);
		}
		setDropdownOpen(false);
		setKeyboardSelectedIndex(-1);
		if (enableSearch) {
			setSearchQuery("");
		}
	}, [onSelect, enableSearch]);

	useEffect(() => {
		if (!isDropdownOpen) return;

		const list = enableSearch ? filteredOptions : options;
		const selectedIndex = list.findIndex((option) => {
			const label = typeof option === "string" ? option : option.label;
			const value = typeof option === "string" ? option : option.value;
			return selectedValue !== undefined? value === selectedValue: label === selectedOption || label === selectedLabel;
		});

		setKeyboardSelectedIndex(selectedIndex >= 0 ? selectedIndex : 0);
	}, [isDropdownOpen, options, filteredOptions, enableSearch, selectedLabel, selectedOption, selectedValue]);

	// Keyboard navigation when search is enabled (matches DropdownNoLabel / time lists)
	useEffect(() => {
		if (!isDropdownOpen || !enableSearch) return;

		const handleDocumentKeyDown = (event) => {
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					if (filteredOptions.length === 0) break;
					setKeyboardSelectedIndex((prev) =>
						Math.min(prev + 1, filteredOptions.length - 1)
					);
					break;
				case "ArrowUp":
					event.preventDefault();
					setKeyboardSelectedIndex((prev) => Math.max(prev - 1, 0));
					break;
				case "Enter":
					event.preventDefault();
					if (
						keyboardSelectedIndex >= 0 &&
						keyboardSelectedIndex < filteredOptions.length
					) {
						handleSelect(filteredOptions[keyboardSelectedIndex]);
					}
					break;
				case "Escape":
					event.preventDefault();
					setDropdownOpen(false);
					setKeyboardSelectedIndex(-1);
					setSearchQuery("");
					break;
				default:
					break;
			}
		};

		document.addEventListener("keydown", handleDocumentKeyDown);
		return () => document.removeEventListener("keydown", handleDocumentKeyDown);
	}, [isDropdownOpen, enableSearch, filteredOptions, handleSelect, keyboardSelectedIndex]);

	// Clamp highlight when the user filters (avoid out-of-range index)
	useEffect(() => {
		if (!isDropdownOpen || !enableSearch || searchQuery === "") return;
		setKeyboardSelectedIndex((prev) => {
			if (filteredOptions.length === 0) return -1;
			return Math.min(Math.max(prev, 0), filteredOptions.length - 1);
		});
	}, [searchQuery, filteredOptions, isDropdownOpen, enableSearch]);

	// Focus search when opening searchable dropdown
	useEffect(() => {
		if (isDropdownOpen && enableSearch && searchInputRef.current) {
			const t = setTimeout(() => searchInputRef.current?.focus(), 0);
			return () => clearTimeout(t);
		}
	}, [isDropdownOpen, enableSearch]);

	useEffect(() => {
		if (!isDropdownOpen) return;

		const selectedElement = dropdownMenuRef.current?.querySelector(`[data-keyboard-selected="true"]`);
		if (selectedElement) {
			selectedElement.scrollIntoView({behavior: "smooth",block: "center",});
		}
	}, [isDropdownOpen, keyboardSelectedIndex]);

	const toggleDropdown = useCallback(() => {
		if (!isDropdownOpen && onOpen) onOpen();
		setDropdownOpen((prev) => {
			const next = !prev;
			if (!next && enableSearch) {
				setSearchQuery("");
			}
			return next;
		});
		setTimeout(() => {
			if (!enableSearch) {
				dropdownRef.current?.focus();
			}
		}, 0);
	}, [isDropdownOpen, onOpen, enableSearch]);

	const handleKeyDown = useCallback((event) => {
		if (enableSearch && isDropdownOpen) {
			return;
		}

		const listOptions = enableSearch ? filteredOptions : options;
		const maxIndex = listOptions.length - 1;

		if (!isDropdownOpen) {
			if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggleDropdown();
			}
			return;
		}

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				setKeyboardSelectedIndex((prev) => Math.min(prev + 1, maxIndex));
				break;
			case "ArrowUp":
				event.preventDefault();
				setKeyboardSelectedIndex((prev) => Math.max(prev - 1, 0));
				break;
			case "Enter":
				event.preventDefault();
				if (keyboardSelectedIndex >= 0 && keyboardSelectedIndex < listOptions.length) {
					handleSelect(listOptions[keyboardSelectedIndex]);
				}
				break;
			case "Escape":
				event.preventDefault();
				setDropdownOpen(false);
				setKeyboardSelectedIndex(-1);
				if (enableSearch) {
					setSearchQuery("");
				}
				break;
			default:
				break;
		}
	}, [enableSearch, filteredOptions, handleSelect, isDropdownOpen, keyboardSelectedIndex, options, toggleDropdown]);

	// console.log("Dropdown Props:", { selectedLabel, selectedOption, options });
	const displayOption = selectedOption || placeholder;
	// console.log("Displaying Option:", displayOption);

	return (
		<div
			ref={dropdownRef}
			className={`${dropdownClass} commonDropdownNoImage`}
			tabIndex={0}
			onKeyDown={handleKeyDown}>
			<div className={`${styles.dropdownToggle} dropdownToggle ${dropdownToggleClass} ${!selectedLabel ? placeholderClass : ""}`}
				onMouseDown={(e) => e.preventDefault()}
				onClick={toggleDropdown}>
				{labelText} {displayOption}
				<DropDownIcon 
					size={12} 
					color="#000"
					strokeWidth="2" 
					className={`${styles.dropdownIcon} ${isDropdownOpen ? styles.rotated : ''}`}
				/>
			</div>
			{isDropdownOpen && (
				<ul className={`${dropdownMenuClass} dropdownMenu`} ref={dropdownMenuRef}>
					{enableSearch && (
						<li className="dropdownItem" style={{ padding: "0", borderBottom: "1px solid #eee" }}>
							<SearchBar
								ref={searchInputRef}
								placeholder={searchPlaceholder}
								value={searchQuery}
								onChange={(e) => {
									setSearchQuery(e.target.value);
								}}
								className="border-0"
							/>
						</li>
					)}
					{enableSearch && filteredOptions.length === 0 ? (
						<li className="dropdownItem" style={{ color: "#999", fontStyle: "italic" }}>
							No options found
						</li>
					) : (
					(enableSearch ? filteredOptions : options).map((option, index) => {
						const label = typeof option === "string" ? option : option.label;
						const optionValue = typeof option === "string" ? option : option.value;
						const isSelected = selectedValue !== undefined && optionValue === selectedValue;
						const isKeyboardSelected = keyboardSelectedIndex === index;

						return (
							<li
								key={typeof optionValue === "string" || typeof optionValue === "number" ? String(optionValue) : index}
								className={`dropdownItem ${dropdownOptionsClass} ${isSelected ? styles.selectedItem : ""} ${isKeyboardSelected ? styles.keyboardSelected : ""}`}
								data-selected={isSelected}
								data-keyboard-selected={isKeyboardSelected}
								onClick={() => {
									if (!isSelected) {
										handleSelect(option);
									}
								}}
								style={{
									cursor: isSelected ? "default" : "pointer",
								}}
								>
								<span
									style={{
										display: "flex",
										alignItems: "center",
									}}>
									{showGenderCircle && option.gender && (
										<span
											className={`genderCircle ${
												option.gender.toLowerCase() ===
												"female"
													? "female"
													: "male"
											}`}>
											{option.gender.toLowerCase() ===
											"female"
												? "F"
												: "M"}
										</span>
									)}
									{label}
								</span>
							</li>
						);
					})
					)}
				</ul>
			)}
		</div>
	);
};

export default DropdownNoImage;
